import React, { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Menu, X, User, LogOut, LayoutDashboard, Shield, Settings } from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { logoUrl, companyName, menuItems } = useSite();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const languages = [
    { code: 'ja', name: '日本語', flagUrl: 'https://flagcdn.com/w40/jp.png' },
    { code: 'en', name: 'English', flagUrl: 'https://flagcdn.com/w40/us.png' },
    { code: 'pt', name: 'Português', flagUrl: 'https://flagcdn.com/w40/br.png' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <header className="glass-nav fixed top-0 left-0 right-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              {logoUrl && (
                <img src={logoUrl} alt="" className="h-8 w-8 object-contain rounded" />
              )}
              <span className="font-heading text-xl text-white tracking-tight">{companyName}</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {menuItems.map((item, idx) => {
                const label = i18n.language === 'en' ? (item.label_en || item.label) :
                              i18n.language === 'pt' ? (item.label_pt || item.label) : item.label;
                return (
                  <Link
                    key={idx}
                    to={item.url}
                    className="text-zinc-400 hover:text-white transition-colors text-sm"
                    data-testid={`nav-menu-${idx}`}
                  >
                    {label}
                  </Link>
                );
              })}
              {user && user.role !== 'admin' && (
                <Link
                  to="/dashboard"
                  className="text-zinc-400 hover:text-white transition-colors text-sm"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Link>
              )}
              {user && user.role === 'admin' && (
                <Link
                  to="/admin"
                  className="text-zinc-400 hover:text-white transition-colors text-sm"
                  data-testid="nav-admin"
                >
                  {t('nav.admin')}
                </Link>
              )}
            </div>

            {/* Right side */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 gap-2 px-3" data-testid="language-switcher">
                    <img src={languages.find(l => l.code === i18n.language)?.flagUrl} alt="" className="w-5 h-auto rounded-sm" />
                    <span className="text-sm">{languages.find(l => l.code === i18n.language)?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#1C1C1C] border-white/10 min-w-[160px]">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`text-zinc-300 hover:text-white hover:bg-white/10 cursor-pointer py-2.5 ${
                        i18n.language === lang.code ? 'bg-white/5 text-white' : ''
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <img src={lang.flagUrl} alt="" className="w-5 h-auto rounded-sm mr-3" />
                      <span className="font-medium">{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Auth buttons */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="user-menu">
                      <User className="h-4 w-4 mr-2" />
                      {user.name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1C1C1C] border-white/10">
                    {user.role === 'admin' ? (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate('/admin')}
                          className="text-zinc-300 hover:text-white hover:bg-white/10 cursor-pointer"
                          data-testid="menu-admin"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          {t('nav.admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={handleLogout}
                          className="text-zinc-300 hover:text-white hover:bg-white/10 cursor-pointer"
                          data-testid="menu-logout"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          {t('nav.logout')}
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate('/dashboard')}
                          className="text-zinc-300 hover:text-white hover:bg-white/10 cursor-pointer"
                          data-testid="menu-dashboard"
                        >
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          {t('nav.dashboard')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/kyc')}
                          className="text-zinc-300 hover:text-white hover:bg-white/10 cursor-pointer"
                          data-testid="menu-kyc"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          {t('kyc.title')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={handleLogout}
                          className="text-zinc-300 hover:text-white hover:bg-white/10 cursor-pointer"
                          data-testid="menu-logout"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          {t('nav.logout')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="nav-login">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="nav-register">
                      {t('nav.register')}
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-zinc-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-white/10">
              <div className="flex flex-col space-y-4">
                {menuItems.map((item, idx) => {
                  const label = i18n.language === 'en' ? (item.label_en || item.label) :
                                i18n.language === 'pt' ? (item.label_pt || item.label) : item.label;
                  return (
                    <Link
                      key={idx}
                      to={item.url}
                      className="text-zinc-400 hover:text-white transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {label}
                    </Link>
                  );
                })}
                {user ? (
                  <>
                    {user.role === 'admin' ? (
                      <Link
                        to="/admin"
                        className="text-zinc-400 hover:text-white transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t('nav.admin')}
                      </Link>
                    ) : (
                      <Link
                        to="/dashboard"
                        className="text-zinc-400 hover:text-white transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t('nav.dashboard')}
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="text-zinc-400 hover:text-white transition-colors text-left"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-zinc-400 hover:text-white transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="text-zinc-400 hover:text-white transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.register')}
                    </Link>
                  </>
                )}
                <div className="flex space-x-2 pt-2 border-t border-white/10">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${
                        i18n.language === lang.code
                          ? 'bg-white text-black'
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      <img src={lang.flagUrl} alt={lang.name} className="w-5 h-auto rounded-sm" />
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2.5">
                {logoUrl && (
                  <img src={logoUrl} alt="" className="h-8 w-8 object-contain rounded" />
                )}
                <span className="font-heading text-xl text-white tracking-tight">{companyName}</span>
              </div>
              <p className="text-zinc-500 text-sm mt-4 max-w-md">
                {t('hero.subtitle')}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2">
                <li><Link to="/page/about" className="text-zinc-500 hover:text-white text-sm">{t('footer.company')}</Link></li>
                <li><Link to="/contact" className="text-zinc-500 hover:text-white text-sm">{t('footer.contact')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.terms')}</h4>
              <ul className="space-y-2">
                <li><Link to="/page/terms" className="text-zinc-500 hover:text-white text-sm">{t('footer.terms')}</Link></li>
                <li><Link to="/page/privacy" className="text-zinc-500 hover:text-white text-sm">{t('footer.privacy')}</Link></li>
                <li><Link to="/page/risk" className="text-zinc-500 hover:text-white text-sm">{t('footer.risk')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-zinc-500 text-sm">
            {t('footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
